import React from 'react';
import { Mail } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import type { TimesheetDeadlineSettings } from '@/types/timesheetSettings';

const DAYS_OF_WEEK = [
  { value: 1 as const, label: 'Mon' },
  { value: 2 as const, label: 'Tue' },
  { value: 3 as const, label: 'Wed' },
  { value: 4 as const, label: 'Thu' },
  { value: 5 as const, label: 'Fri' },
  { value: 6 as const, label: 'Sat' },
  { value: 7 as const, label: 'Sun' },
] as const;

interface ManagerDigestSettingsProps {
  settings: TimesheetDeadlineSettings;
  onChange: (settings: TimesheetDeadlineSettings, isValid: boolean) => void;
}

// ---------------------------------------------------------------------------
// Setting card — matches Submission Reminders card pattern
// ---------------------------------------------------------------------------

interface SettingCardProps {
  id: string;
  label: string;
  description: string;
  control: React.ReactNode;
  onClick?: () => void;
}

function SettingCard({ id, label, description, control, onClick }: SettingCardProps) {
  return (
    <div
      className={`flex items-center justify-between gap-4 p-4 rounded-lg bg-background transition-colors
        ${onClick ? 'cursor-pointer select-none hover:bg-accent/50' : ''}
      `}
      onClick={onClick}
    >
      <div className="flex-1 min-w-0">
        <Label
          htmlFor={id}
          className={`text-sm font-medium leading-snug ${onClick ? 'cursor-pointer select-none' : ''}`}
        >
          {label}
        </Label>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
      </div>
      <div onClick={(e) => e.stopPropagation()} className="shrink-0">
        {control}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ManagerDigestSettings: React.FC<ManagerDigestSettingsProps> = ({
  settings,
  onChange,
}) => {
  const enableManagerDigest = settings.enableManagerDigest ?? false;
  const digestDayOfWeek = settings.digestDayOfWeek ?? 1;

  const handleToggleChange = (checked: boolean) => {
    const newSettings: TimesheetDeadlineSettings = {
      ...settings,
      enableManagerDigest: checked,
    };
    onChange(newSettings, true);
  };

  const handleDayChange = (day: 1 | 2 | 3 | 4 | 5 | 6 | 7) => {
    const newSettings: TimesheetDeadlineSettings = {
      ...settings,
      digestDayOfWeek: day,
    };
    onChange(newSettings, true);
  };

  return (
    <Accordion type="multiple" defaultValue={['manager-digest']} className="space-y-4">
      <AccordionItem value="manager-digest" className="border rounded-lg">
        <AccordionTrigger className="px-4 hover:no-underline">
          <div className="flex items-center gap-3 text-left">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Mail className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="font-semibold">Manager Weekly Digest</p>
              <p className="text-xs text-muted-foreground font-normal mt-0.5">
                Configure weekly summaries for managers
              </p>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pt-2 pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <SettingCard
              id="enableManagerDigest"
              label="Weekly digest for managers"
              description="Enable weekly summaries for managers with outstanding team timesheets"
              control={
                <Checkbox
                  id="enableManagerDigest"
                  checked={enableManagerDigest}
                  onCheckedChange={(v) => handleToggleChange(!!v)}
                />
              }
              onClick={() => handleToggleChange(!enableManagerDigest)}
            />
          </div>

          {/* Day of week selector — only shown when checkbox is on */}
          {enableManagerDigest && (
            <div className="pl-4 border-l-2 border-muted space-y-3 mt-4">
              <Label>Send digest on:</Label>

              {/* Segmented day picker */}
              <div className="flex gap-1 flex-wrap">
                {DAYS_OF_WEEK.map((day) => (
                  <Button
                    key={day.value}
                    type="button"
                    variant={digestDayOfWeek === day.value ? 'default' : 'outline'}
                    size="sm"
                    className="w-12"
                    onClick={() => handleDayChange(day.value)}
                    aria-pressed={digestDayOfWeek === day.value}
                  >
                    {day.label}
                  </Button>
                ))}
              </div>

              <p className="text-xs text-muted-foreground">
                The digest will be sent at 07:00 UTC on the selected day.
              </p>

              <p className="text-sm text-muted-foreground">
                Digests are sent to managers with direct reports who have outstanding timesheets.
              </p>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
